import { useEffect, useState } from "react";
import { Search, Coins, Gift, History, TrendingUp, TrendingDown } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const formatBRL = (cents) => `R$ ${(cents / 100).toFixed(2).replace(".", ",")}`;
const formatDate = (iso) => new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit" });

const reasonLabel = (r) => {
  const m = {
    welcome_trial:      "🎁 Trial cortesia",
    admin_grant:        "👤 Concedido pelo admin",
    admin:              "👤 Admin",
    order_reserve:      "🛒 Consumido em pedido",
    "refund:fulfillment_failed": "↩️ Refund (falha)",
  };
  return m[r] || r;
};

export default function AdminCreditosMineral() {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [grantAmount, setGrantAmount] = useState(1);
  const [grantReason, setGrantReason] = useState("");
  const [granting, setGranting] = useState(false);

  const loadUsers = async (q = "") => {
    setLoadingUsers(true);
    try {
      const r = await api.get(`/mineral/credits/admin/users${q ? `?search=${encodeURIComponent(q)}` : ""}`);
      setUsers(r.data.users || []);
    } catch { toast.error("Falha ao carregar usuários"); }
    finally { setLoadingUsers(false); }
  };

  const loadLedger = async (userId = null) => {
    try {
      const url = userId ? `/mineral/credits/admin/ledger?user_id=${userId}&limit=100`
                          : `/mineral/credits/admin/ledger?limit=100`;
      const r = await api.get(url);
      setLedger(r.data.rows || []);
    } catch { toast.error("Falha ao carregar ledger"); }
  };

  useEffect(() => { loadUsers(); loadLedger(); }, []);

  const grant = async (e) => {
    e.preventDefault();
    if (!selected) return;
    setGranting(true);
    try {
      await api.post("/mineral/credits/admin/grant", {
        user_id: selected.user_id,
        product_code: "MINERAL_360",
        amount: Number(grantAmount),
        reason: grantReason || "admin_grant",
      });
      toast.success(`+${grantAmount} crédito(s) para ${selected.email}`);
      setGrantAmount(1); setGrantReason("");
      await loadUsers(search);
      await loadLedger(selected.user_id);
      // Refresh selected balances
      const rr = await api.get(`/mineral/credits/admin/user/${selected.user_id}`);
      setSelected({ ...selected, balances: rr.data.balances, total_balance: rr.data.balances.reduce((s,b)=>s+b.balance,0) });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao conceder");
    } finally { setGranting(false); }
  };

  return (
    <div className="p-3 sm:p-6 lg:p-8 space-y-6" data-testid="admin-creditos-mineral">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl inline-flex items-center justify-center"
             style={{ background: "linear-gradient(135deg, #0B1B2F, #071B34)" }}>
          <Coins className="w-5 h-5" style={{ color: "#F0D37A" }}/>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Admin</div>
          <h1 className="font-heading text-3xl font-semibold text-slate-900">Créditos Mineral</h1>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Coluna Usuários */}
        <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
          <div className="flex items-center gap-2 mb-3">
            <Search className="w-4 h-4 text-slate-400"/>
            <input value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && loadUsers(search)}
              placeholder="Buscar por e-mail, nome, user_id…"
              data-testid="input-search-user"
              className="flex-1 h-10 px-3 rounded-lg bg-slate-50 border border-slate-200 text-sm"/>
            <button onClick={() => loadUsers(search)}
              className="h-10 px-4 rounded-lg text-sm font-medium text-white transition"
              style={{ background: "#0B1B2F" }}>
              Buscar
            </button>
          </div>
          <div className="max-h-[420px] overflow-y-auto divide-y divide-slate-100">
            {loadingUsers ? (
              <div className="p-8 text-center text-slate-400 text-sm">Carregando…</div>
            ) : users.length === 0 ? (
              <div className="p-8 text-center text-slate-400 text-sm">Nenhum usuário encontrado.</div>
            ) : users.map(u => (
              <button key={u.user_id} data-testid={`admin-user-${u.user_id}`}
                onClick={() => { setSelected(u); loadLedger(u.user_id); }}
                className={`w-full text-left p-3 hover:bg-slate-50 flex items-center justify-between transition ${selected?.user_id === u.user_id ? "bg-amber-50" : ""}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">{u.name || u.email}</div>
                  <div className="text-xs text-slate-500 truncate">{u.email}</div>
                </div>
                <div className="text-right shrink-0 ml-3">
                  <div className="text-sm font-bold" style={{ color: u.total_balance > 0 ? "#B45309" : "#94a3b8" }}>
                    {u.total_balance} crédito{u.total_balance === 1 ? "" : "s"}
                  </div>
                  {u.subscription_active && (
                    <div className="text-[10px] font-semibold text-emerald-600">assinante</div>
                  )}
                </div>
              </button>
            ))}
          </div>
        </section>

        {/* Coluna Grant + Detalhes */}
        <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
          {!selected ? (
            <div className="p-12 text-center text-slate-400 text-sm">
              Selecione um usuário para conceder créditos ou ver o ledger.
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-4">
                <Gift className="w-4 h-4" style={{ color: "#D4AF37" }}/>
                <div className="font-heading text-base font-semibold text-slate-900">
                  Conceder crédito · {selected.name || selected.email}
                </div>
              </div>
              <form onSubmit={grant} className="space-y-3">
                <div className="flex gap-2">
                  <div className="flex-1">
                    <label className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Quantidade</label>
                    <input type="number" min={1} max={100} value={grantAmount}
                      onChange={(e) => setGrantAmount(e.target.value)}
                      data-testid="input-grant-amount"
                      className="w-full h-10 px-3 rounded-lg border border-slate-200 mt-1 text-sm"/>
                  </div>
                  <div className="flex-1">
                    <label className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Motivo (opcional)</label>
                    <input value={grantReason}
                      onChange={(e) => setGrantReason(e.target.value)}
                      placeholder="ex: bônus cortesia"
                      data-testid="input-grant-reason"
                      className="w-full h-10 px-3 rounded-lg border border-slate-200 mt-1 text-sm"/>
                  </div>
                </div>
                <button type="submit" disabled={granting} data-testid="btn-grant-credits"
                  className="w-full h-10 rounded-lg font-semibold text-sm shadow-sm transition disabled:opacity-60"
                  style={{ background: "linear-gradient(180deg,#F5DE8C,#D4AF37)", color: "#0B1B2F" }}>
                  {granting ? "Concedendo…" : `Conceder ${grantAmount} crédito(s)`}
                </button>
              </form>

              <div className="mt-6 flex items-center gap-2 mb-2">
                <History className="w-4 h-4 text-slate-500"/>
                <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
                  Ledger · últimas 100
                </div>
              </div>
              <div className="max-h-[280px] overflow-y-auto text-sm divide-y divide-slate-100">
                {ledger.length === 0 ? (
                  <div className="p-8 text-center text-slate-400 text-sm">Nenhum registro.</div>
                ) : ledger.map((row, i) => (
                  <div key={i} className="py-2 flex items-center gap-3">
                    {row.delta > 0
                      ? <TrendingUp className="w-4 h-4 text-emerald-500 shrink-0"/>
                      : <TrendingDown className="w-4 h-4 text-rose-500 shrink-0"/>}
                    <div className="flex-1 min-w-0">
                      <div className="text-slate-900 truncate">{reasonLabel(row.reason)}</div>
                      <div className="text-[11px] text-slate-400 truncate">
                        {formatDate(row.at)} {row.order_id && `· ${row.order_id}`}
                      </div>
                    </div>
                    <div className={`font-bold text-sm shrink-0 ${row.delta > 0 ? "text-emerald-600" : "text-rose-600"}`}>
                      {row.delta > 0 ? "+" : ""}{row.delta}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
